/**
 * BrandedPageSkeleton — GoalDigger-branded loading placeholder used while a
 * page loads. Soft glowing blocks in brand tones instead of a bare spinner.
 */
import { cn } from "@/lib/utils";

type Tone = "goals" | "creator" | "business";

const TONE_GLOW: Record<Tone, string> = {
  goals: "from-amber-300/25 via-orange-300/15 to-rose-300/20",
  creator: "from-fuchsia-400/25 via-violet-400/15 to-orange-300/20",
  business: "from-emerald-300/25 via-teal-300/15 to-sky-300/20",
};

function Block({ className }: { className?: string }) {
  return <div className={cn("rounded-2xl bg-muted/70 animate-pulse", className)} />;
}

export function BrandedPageSkeleton({ tone = "goals" }: { tone?: Tone }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading"
      className="relative min-h-screen overflow-hidden bg-background"
    >
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-br blur-2xl",
          TONE_GLOW[tone],
        )}
      />
      <div className="relative mx-auto max-w-3xl space-y-5 px-4 pt-8 pb-24">
        <div className="flex items-center gap-3">
          <Block className="h-12 w-12 rounded-2xl" />
          <div className="flex-1 space-y-2">
            <Block className="h-4 w-1/3 rounded-full" />
            <Block className="h-3 w-1/2 rounded-full" />
          </div>
        </div>
        <Block className="h-40 w-full rounded-3xl" />
        <div className="grid grid-cols-3 gap-3">
          <Block className="h-20" />
          <Block className="h-20" />
          <Block className="h-20" />
        </div>
        <Block className="h-24 w-full" />
        <Block className="h-24 w-full" />
        <p className="pt-2 text-center text-xs text-muted-foreground">Getting things ready…</p>
      </div>
    </div>
  );
}

export default BrandedPageSkeleton;
